// ═══════════════════════════════════════════════════════════════════
//  useProcessEngineCapabilities — Tier-aware resolver for /processengine.
// ═══════════════════════════════════════════════════════════════════
//
//  Mirrors `useAtlasCapabilities` shape. Centralises the gate so
//  the page can render an upsell card when the user lacks the tier,
//  hide the "Run live" button when execute is gated, etc.
//
//  Defense-in-depth: build-time flag (`IS_PROCESS_ENGINE_BUILD`)
//  also disables the route entirely.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { usePlan } from '@/hooks/usePlan';
import { canUse, type PlanTier } from '@/lib/planTiers';
import { IS_PROCESS_ENGINE_BUILD } from '@/lib/buildMode';
import { useSystemSwitches } from '@/hooks/useSystemSwitches';

export interface ProcessEngineCapabilities {
  /** Build-flag gate. False ⇒ route is unreachable. */
  buildEnabled: boolean;
  /** Can view / design process flows (Pro+). False ⇒ upsell. */
  processEngineEnabled: boolean;
  /** Can run a flow live with real side effects (Max+). */
  processEngineExecute: boolean;
  /** AI binding-suggester is available (Max+). */
  processEngineAI: boolean;
  /** Daily live-run quota (0 = blocked). */
  maxRunsPerDay: number;
  /** Lowest tier that unlocks `processEngineEnabled`. */
  processEngineMinTier: PlanTier;
  /** Lowest tier that unlocks live execution. */
  executeMinTier: PlanTier;
  /** Lowest tier that unlocks AI suggester. */
  aiMinTier: PlanTier;
  /** Current tier (for messaging). */
  currentTier: PlanTier;
}

export function useProcessEngineCapabilities(): ProcessEngineCapabilities {
  const { tier, limits } = usePlan();
  const { switches } = useSystemSwitches();

  return useMemo(() => {
    const systemOn = IS_PROCESS_ENGINE_BUILD && switches.processEngine;
    const enabledGate = canUse(limits, 'process-engine');
    const executeGate = canUse(limits, 'process-engine-execute');
    const aiGate = canUse(limits, 'process-engine-ai');

    return {
      buildEnabled: IS_PROCESS_ENGINE_BUILD,
      processEngineEnabled: systemOn && limits.processEngineEnabled,
      processEngineExecute: systemOn && limits.processEngineExecute,
      // PE-AI gates on both PE-on AND AI-on (it calls LLM endpoints).
      processEngineAI: systemOn && limits.processEngineAI && switches.ai,
      maxRunsPerDay: limits.processEngineMaxRunsPerDay,
      processEngineMinTier: (enabledGate.requiredTier ?? 'pro') as PlanTier,
      executeMinTier: (executeGate.requiredTier ?? 'max') as PlanTier,
      aiMinTier: (aiGate.requiredTier ?? 'max') as PlanTier,
      currentTier: tier,
    };
  }, [limits, tier, switches.processEngine, switches.ai]);
}
